'use client';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { forwardRef, useState, useRef, useEffect } from 'react';
import { isInputLike } from './fieldObserver.js';
import { ValidationMessage } from '../ValidationMessage/ValidationMessage.js';
import { Paragraph } from '../Paragraph/Paragraph.js';

const label = (text, count) => text.replace('%d', Math.abs(count).toString());
/**
 * FieldCounter component, used to display a counter for a form field.
 *
 * @example
 * <Field>
 *   <Input />
 *   <Field.Counter limit={100} under='%d tegn igjen' over='%d tegn for mye' />
 * </Field>
 */
const FieldCounter = forwardRef(function FieldCounter({ limit, under = '%d tegn igjen', over = '%d tegn for mye', ...rest }, ref) {
    const [count, setCount] = useState(0);
    const counterRef = useRef(null);
    const hasExceededLimit = count > limit;
    const remainder = limit - count;
    useEffect(() => {
        const field = counterRef.current?.closest('.ds-field');
        const input = Array.from(field?.getElementsByTagName('*') || []).find(isInputLike);
        const onInput = ({ target }) => {
            if (isInputLike(target))
                setCount(target.value.length);
        };
        if (input)
            onInput({ target: input }); // Initial setup
        field?.addEventListener('input', onInput);
        return () => field?.removeEventListener('input', onInput);
    }, [setCount]);
    return (jsxs(Fragment, { children: [jsx("div", { "data-field": 'description', className: 'ds-sr-only', "aria-live": 'polite', ref: counterRef, children: hasExceededLimit && label(over, remainder) }), hasExceededLimit ? (jsx(ValidationMessage, { ref: ref, ...rest, children: label(over, remainder) })) : (jsx(Paragraph, { ref: ref, ...rest, "data-field": 'validation', children: label(under, remainder) }))] }));
});

export { FieldCounter };
